import React from "react";
import clsx from "clsx";
import styles from "./match.module.scss";
import { DependencyTextMap, PositionedBracket, Seed } from "core/models/Bracket";
import ArrowTail from "shared/Icons/ArrowTail";
import FriendIcon from "shared/Icons/Friend";

interface BracketBoxProps {
  positionedBracket: PositionedBracket;
  width: number;
  height: number;
  onFocus: (id: string) => void;
  onClick?: (seed: Seed) => void;
  hoveredTeamKey?: string;
  futureTeamMap: DependencyTextMap;
}

export const Match: React.FC<BracketBoxProps> = ({
  positionedBracket,
  width,
  height,
  onFocus,
  onClick,
  hoveredTeamKey,
  futureTeamMap,
}) => {
  const teams = positionedBracket.seed.teams || [];
  const teamKeyOf = (t: any): string | null =>
    t ? t.id ?? t._id ?? t.uuid ?? t.teamId ?? t.slug ?? t.name ?? null : null;

  const teamKey0 = teamKeyOf(teams?.[0]);
  const teamKey1 = teamKeyOf(teams?.[1]);
  const isTeam0Highlighted = hoveredTeamKey && teamKey0 === hoveredTeamKey;
  const isTeam1Highlighted = hoveredTeamKey && teamKey1 === hoveredTeamKey;
  const _regionClass =
    positionedBracket.region === "upper"
      ? styles.upper
      : positionedBracket.region === "lower"
      ? styles.lower
      : positionedBracket.region === "final"
      ? styles.final
      : styles.third;

  function handleClick() {
    onClick?.(positionedBracket.seed);
  }

  const matchRef = React.useRef<HTMLDivElement | null>(null);
  function setHoveredTeam(index: 0 | 1 | null) {
    const evt = new CustomEvent("brackets:hover-team", {
      detail: {
        fromId: positionedBracket.id,
        index,
      },
      bubbles: true,
    });
    matchRef.current?.dispatchEvent(evt);
  }

  return (
    <div
      ref={matchRef}
      data-match
      data-bracket-id={positionedBracket.id}
      onDoubleClick={() => onFocus(positionedBracket.id)}
      className={styles.container}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      style={{
        left: positionedBracket.x,
        top: positionedBracket.y,
        width,
        height,
      }}
    >
      <div className={styles.header}>
        <p
          className={styles.title}
        >{`Match ${positionedBracket?.seed?.matchNumber}`}</p>
        <div className={styles.out}>
          <ArrowTail fontSize="8px" rotate={135} />
        </div>
      </div>
      <div className={styles.teams} onMouseLeave={() => setHoveredTeam(null)}>
        <div
          className={clsx(
            styles.team,
            styles.up,
            isTeam0Highlighted && styles.teamHighlighted
          )}
          onMouseEnter={() => setHoveredTeam(0)}
          onFocus={() => setHoveredTeam(0)}
          onBlur={() => setHoveredTeam(null)}
          data-team-highlighted={isTeam0Highlighted ? "true" : undefined}
        >
          <div className={styles.details}>
            <div className={styles.seed}>{teams?.[0]?.seed}</div>
            {teams?.[0]?.image ? (
              <img
                width={28}
                height={28}
                className={styles["team-logo"]}
                src={teams?.[0]?.image}
                alt="profile img"
              />
            ) : (
              <FriendIcon fontSize="28px" color='hsla(0, 0%, 100%, .2)' />
            )}

            <div className={styles["team-name"]}>
              <p>
                {teams?.[0]?.name ||
                  futureTeamMap?.get(positionedBracket.id)?.[0]}
              </p>
            </div>
          </div>
          <p className={styles.score}>
            {positionedBracket.seed.isCompleted && teams?.[0]?.score}
          </p>
        </div>
        <div
          className={clsx(
            styles.team,
            styles.down,
            isTeam1Highlighted && styles.teamHighlighted
          )}
          onMouseEnter={() => setHoveredTeam(1)}
          onFocus={() => setHoveredTeam(1)}
          onBlur={() => setHoveredTeam(null)}
          data-team-highlighted={isTeam1Highlighted ? "true" : undefined}
        >
          <div className={styles.details}>
            <div className={styles.seed}>{teams?.[1]?.seed}</div>
            {teams?.[1]?.image ? (
              <img
                width={28}
                height={28}
                className={styles["team-logo"]}
                src={teams?.[1]?.image}
                alt="profile img"
              />
            ) : (
              <FriendIcon fontSize="28px" color='hsla(0, 0%, 100%, .2)' />
            )}
            <div className={styles["team-name"]}>
              <p>
                {teams?.[1]?.name ||
                  futureTeamMap?.get(positionedBracket.id)?.[1]}
              </p>
            </div>
          </div>
          <p className={styles.score}>
            {positionedBracket.seed.isCompleted && teams?.[1]?.score}
          </p>
        </div>
      </div>
    </div>
  );
};
